import {
  archiveIncomingDocument,
  attachOrOcrIncomingDocument,
  type SearchResultIncomingDocument
} from "app/employee/state/incdoc/mutations";
import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardSubtitle,
  IonCardTitle,
  IonIcon,
  useIonActionSheet,
  useIonAlert
} from "@ionic/react";
import {DateDisplay} from "app/employee/DateDisplay";
import {AttachmentCollection} from "app/employee/att/AttachmentCollection";
import {Trans, useTranslation} from "react-i18next";
import {
  barcodeOutline,
  closeOutline,
  ellipsisVerticalCircleOutline,
  lockClosedOutline,
  pencilOutline,
  trashOutline
} from "ionicons/icons";
import {useCallback, useRef} from "react";
import {DocumentNotes} from "app/employee/incdoc/DocumentNotes";
import {FlexColumn} from "app/employee/flexUtils";
import {EditContainer} from "app/employee/incdoc/EditContainer";
import {usePermissions} from "app/employee/hooks/usePermissions";
import {LOCALE, luxonToShortDate, parseAnyDate} from "app/utils/dates";
import {displaySupplier} from "app/components/admin/employee/utils";
import {nativeAppAdapter} from "app/employee/nativeApp/FSAcademyAndroidAdapter";
import {FstIonModal} from "app/employee/incdoc/FstIonModal";
import {PurchaseDocumentPreview} from "app/employee/incdoc/PurchaseDocumentPreview";
import {isNil} from "app/utils/stdlib";

interface ContextMenuProps {
  onDelete: () => void | Promise<void>;
  onEdit: () => void | Promise<void>;
  onPreview: (() => void | Promise<void>) | null;
  canEdit: boolean;
}

const useContextMenu = ({onDelete, onEdit, onPreview, canEdit}: ContextMenuProps) => {
  const {t} = useTranslation();
  const [presentActionSheet] = useIonActionSheet();
  const [presentConfirmation] = useIonAlert();
  const showOptions = useCallback(
    async () =>
      presentActionSheet({
        header: t("incdoc.action.title", {defaultValue: "Действия"}),

        buttons: [
          ...(!isNil(onPreview)
            ? [
                {
                  text: t("incdoc.action.scan-result", {defaultValue: "Виж разпознатия документ"}),
                  icon: barcodeOutline,
                  handler: onPreview
                }
              ]
            : []),
          ...(canEdit
            ? [
                {
                  text: t("incdoc.action.edit", {defaultValue: "Редактирай"}),
                  icon: pencilOutline,
                  handler: onEdit
                },
                {
                  text: t("incdoc.action.delete", {defaultValue: "Изтрий"}),
                  role: "destructive",
                  icon: trashOutline,
                  handler: async () => {
                    await presentConfirmation({
                      header: t("incdoc.action.confirm-delete.title", {
                        defaultValue: "Сигурни ли сте че искате да изтриете този документ?"
                      }),
                      buttons: [
                        {
                          text: t("g.cancel"),
                          role: "cancel"
                        },
                        {
                          text: t("g.delete", {defaultValue: "Изтрий"}),
                          role: "destructive",
                          handler: onDelete
                        }
                      ]
                    });
                  }
                }
              ]
            : []),
          {
            text: t("g.close"),
            icon: closeOutline,
            role: "cancel"
          }
        ]
      }),
    [canEdit, onDelete, onEdit, onPreview, presentActionSheet, presentConfirmation, t]
  );

  return {showOptions, isLocked: !canEdit && isNil(onPreview)};
};

interface Props {
  document: SearchResultIncomingDocument;
  refresh: () => void | Promise<void>;
  editDocument: (document: SearchResultIncomingDocument) => void | Promise<void>;
}

export const DocumentCard = ({document, refresh, editDocument}: Props) => {
  const {t} = useTranslation();
  const purchaseDocModalRef = useRef<HTMLIonModalElement>(null);

  const handleUploadCompleted = useCallback(async () => {
    await attachOrOcrIncomingDocument({incomingDocumentId: document.id});
    await refresh();
  }, [document.id, refresh]);

  const {canEditIncDoc} = usePermissions();

  const supplierName = displaySupplier(t, document.supplier);
  const locationName = document.location?.name;
  const creatorName = document.creator.name;
  const createdAt = document.createdAt;
  const canEdit = canEditIncDoc(document);
  const {showOptions, isLocked} = useContextMenu({
    onDelete: async () => {
      await archiveIncomingDocument(document.id);
      await refresh();
    },
    onEdit: () => {
      editDocument(document);
    },
    onPreview: document.purchaseDocument ? () => purchaseDocModalRef.current?.present() : null,
    canEdit
  });
  // Doubles as the shared PDF's file name, so the date stays on the fixed LOCALE: it keeps the
  // Bulgarian document reference format and avoids the slashes that en-GB/zh-CN dates would put
  // into the file name.
  const name = t("incdoc.doc-name", {
    date: luxonToShortDate(parseAnyDate(document.documentDate), LOCALE),
    number: document.documentNumber
  });
  return (
    <IonCard className="ion-no-margin">
      <EditContainer>
        <IonButton disabled={isLocked} fill="clear" onClick={showOptions}>
          <IonIcon icon={isLocked ? lockClosedOutline : ellipsisVerticalCircleOutline} slot="icon-only" />
        </IonButton>
      </EditContainer>
      <IonCardHeader>
        <IonCardTitle>
          {t("incdoc.doc-no", {defaultValue: "№{{docNum}}", docNum: document.documentNumber})}
        </IonCardTitle>
        <IonCardSubtitle>
          <Trans i18nKey="incdoc.doc-subtitle" t={t}>
            От дата{" "}
            <strong>
              <DateDisplay dt={document.documentDate} />
            </strong>
            , доставчик <strong>{{supplierName}}</strong>, за обект <strong>{{locationName}}</strong>, създаден от{" "}
            <strong>{{creatorName}}</strong> на <DateDisplay dt={createdAt} />
          </Trans>
        </IonCardSubtitle>
      </IonCardHeader>
      <IonCardContent>
        <FlexColumn>
          <AttachmentCollection
            accept={nativeAppAdapter.getScannerMimeType()}
            attCollection={document.attachmentCollection}
            canDelete={false}
            canUpload={canEdit}
            name={name}
            onUploadCompleted={handleUploadCompleted}
          />
          <DocumentNotes notes={document.notes} />
        </FlexColumn>
      </IonCardContent>
      {document.purchaseDocument && (
        <FstIonModal ref={purchaseDocModalRef}>
          <PurchaseDocumentPreview purchaseDocument={document.purchaseDocument} />
        </FstIonModal>
      )}
    </IonCard>
  );
};
